const DEFAULT_WEB2_APP_ORIGIN = "https://app.superwall.com"

const normalizeWeb2Origin = (value: string | undefined) => {
  if (!value) {
    return DEFAULT_WEB2_APP_ORIGIN
  }

  try {
    return new URL(value).origin
  } catch {
    return DEFAULT_WEB2_APP_ORIGIN
  }
}

const WEB2_APP_ORIGIN = normalizeWeb2Origin(process.env.NEXT_PUBLIC_WEB2_APP_ORIGIN)

type DashboardWeb2SearchQuery = Record<string, string | string[] | undefined>

const firstString = (value: string | string[] | undefined) =>
  Array.isArray(value) ? value[0] : value

const toSearchParams = (
  query: DashboardWeb2SearchQuery,
  options: { preserveKeys?: readonly string[] } = {}
) => {
  const search = new URLSearchParams()
  const preserveKeys = new Set(options.preserveKeys ?? [])

  for (const [key, value] of Object.entries(query)) {
    if (
      (!preserveKeys.has(key) &&
        (key === "applicationId" ||
          key === "appUserId" ||
          key === "eventId" ||
          key === "surveyId" ||
          key === "integrationId" ||
          key === "campaignId" ||
          key === "paywallId" ||
          key === "locale")) ||
      value === undefined
    ) {
      continue
    }

    const values = Array.isArray(value) ? value : [value]
    for (const item of values) {
      if (item) {
        search.append(key, item)
      }
    }
  }

  return search
}

const appendSearch = (
  pathname: string,
  query: DashboardWeb2SearchQuery,
  options?: { preserveKeys?: readonly string[] }
) => {
  const search = toSearchParams(query, options)
  const suffix = search.toString()
  return suffix ? `${pathname}?${suffix}` : pathname
}

const parseApplicationId = (value: string | string[] | undefined) => {
  const applicationId = Number(firstString(value))
  return Number.isInteger(applicationId) && applicationId > 0 ? applicationId : null
}

const decodeSegment = (value: string) => {
  try {
    return decodeURIComponent(value)
  } catch {
    return value
  }
}

const encodeSegment = (value: string | undefined) =>
  value ? encodeURIComponent(decodeSegment(value)) : null

const legacySettingsToWeb2Subpage = new Map<string, string | null>([
  ["api-keys", "api-keys"],
  ["keys", "api-keys"],
  ["team", "access"],
  ["stripe-configuration", "stripe"],
  ["advanced", "danger-zone"],
])

export const buildDashboardWeb2ApplicationUrl = ({
  applicationId,
  organizationId,
  path = "dashboard",
}: {
  applicationId: number
  organizationId?: number
  path?: string
}) => {
  if (organizationId == null) {
    return null
  }

  const normalizedPath = path.replace(/^\/+/, "")
  const pathname = normalizedPath
    ? `/${organizationId}/${applicationId}/${normalizedPath}`
    : `/${organizationId}/${applicationId}/`

  return new URL(pathname, WEB2_APP_ORIGIN).toString()
}

export const buildDashboardWeb2UrlFromLegacyRoute = ({
  applicationId,
  organizationId,
  pathname,
  query,
}: {
  applicationId: number
  organizationId?: number
  pathname: string
  query: DashboardWeb2SearchQuery
}) => {
  if (organizationId == null) {
    return null
  }

  const prefix = `/applications/${applicationId}`
  if (pathname !== prefix && !pathname.startsWith(`${prefix}/`)) {
    return null
  }

  const rawSuffix = pathname === prefix ? "" : pathname.replace(`${prefix}/`, "")
  const segments = rawSuffix.split("/").filter(Boolean)
  const [section, second, third] = segments
  let web2Path: string | null = null
  let preserveSearchKeys: readonly string[] = []

  if (!section) {
    web2Path = "dashboard"
  } else if (section === "charts") {
    web2Path = "charts"
  } else if (section === "quickstart") {
    web2Path = "getting-started"
  } else if (section === "users") {
    const appUserId = second === "v2" ? third : second
    const encodedAppUserId = encodeSegment(appUserId)
    web2Path = encodedAppUserId ? `users/${encodedAppUserId}` : "users"
  } else if (section === "events") {
    const encodedEventId = encodeSegment(second)
    web2Path = encodedEventId ? `events/${encodedEventId}` : "users"
    preserveSearchKeys = encodedEventId ? ["appUserId"] : []
  } else if (section === "rules" || section === "campaigns") {
    const encodedCampaignId = encodeSegment(second)
    if (!second) {
      web2Path = "campaigns"
    } else if (second !== "archived" && encodedCampaignId) {
      web2Path = `campaigns/${encodedCampaignId}`
    }
  } else if (section === "paywalls") {
    const encodedPaywallId = encodeSegment(second)

    if (!second || second === "v2") {
      web2Path = "paywalls"
    } else if (second === "new") {
      web2Path = "paywalls/new"
    } else if (third === "duplicate" && encodedPaywallId) {
      web2Path = `paywalls/${encodedPaywallId}/duplicate`
    } else if (third === "app-preview" && encodedPaywallId) {
      web2Path = `paywalls/${encodedPaywallId}/preview`
    }
  } else if (
    section === "assets" ||
    section === "templates" ||
    section === "products" ||
    section === "demand-score"
  ) {
    web2Path = section
  } else if (section === "integrations") {
    if (!second) {
      web2Path = "integrations"
    } else if (second === "retention-messaging") {
      web2Path = "integrations/retention-messaging"
    }
  } else if (section === "surveys") {
    web2Path = second && third ? `surveys/${second}/${third}` : "surveys"
  } else if (section === "settings") {
    if (!second) {
      web2Path = "settings"
    } else if (second === "billing" && !third && firstString(query.legacy) !== "1") {
      web2Path = "settings/billing"
    } else if (legacySettingsToWeb2Subpage.has(second)) {
      const web2SettingsSubpage = legacySettingsToWeb2Subpage.get(second)
      web2Path = web2SettingsSubpage ? `settings/${web2SettingsSubpage}` : "settings"
    }
  }

  if (!web2Path) {
    return null
  }

  const pathnameWithSearch = appendSearch(
    `/${organizationId}/${applicationId}/${web2Path}`,
    query,
    { preserveKeys: preserveSearchKeys }
  )

  return new URL(pathnameWithSearch, WEB2_APP_ORIGIN).toString()
}

export const buildDashboardWeb2UrlFromLegacyRouter = ({
  pathname,
  query,
  organizationId,
}: {
  pathname: string
  query: DashboardWeb2SearchQuery
  organizationId?: number
}) => {
  const applicationId = parseApplicationId(query.applicationId)
  if (!applicationId) {
    return null
  }

  return buildDashboardWeb2UrlFromLegacyRoute({
    applicationId,
    organizationId,
    pathname,
    query,
  })
}
